import { ChangeEvent, FC, Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { Icon, Input, SelectCard, Text, Tooltip, SelectCardCheckBox, Checkbox, Image } from '@/client/components'
import { CartContext } from '@/client/context/cart'
import {
  useGetMaterial,
  useMutatePrices,
  useWindowSize,
  useGetPlusMaterial,
  useGetStandardMaterial,
  useGetSustainableMaterial
} from '@/client/hooks'
import { IFieldOption } from '@/client/types'
import * as S from '../styles'
import { IconName, IconSizes } from '@/client/components/Icon/types'
import { FieldType, SettingsChildProps, getCalculatorConstants } from '../types'
import { List } from '@/client/components'
import { ListItem } from '@/client/components'
import { SelectForCalcMaterials } from '@/client/components/SelectForCalcMaterial'

export const CalculatorStep: FC<SettingsChildProps> = ({
  fields,
  step,
  randomText,
  initialSelection,
  reset,
  setResetFalse,
  serviceType,
  helperText
}) => {
  const { locale } = useRouter()
  const { mutate } = useMutatePrices(locale)
  const { updateSelection, selection } = useContext(CartContext)
  const [keyType, setKeyType] = useState(false)
  const [showDropdown, setShowDropdown] = useState({
    state: false,
    id: null,
    top: '115px',
    index: 0,
    closeCurrent: false
  })
  const [selectedMaterialId, setSelectedMaterialId] = useState(0)
  const { data: materialData } = useGetMaterial(selectedMaterialId, locale)
  const { data: plusMaterial } = useGetPlusMaterial()
  const { data: standardMaterial } = useGetStandardMaterial()
  const { data: sustainableMaterial } = useGetSustainableMaterial()
  const vRadioRef = useRef<Array<HTMLDivElement | null>>([])
  const relativeParentRef = useRef<HTMLDivElement | null>(null)
  const optionWrapperSet = useRef<HTMLDivElement | null>(null)
  const [newIndex, setNewIndex] = useState<number>(0)
  const [maxWidth, setMaxWidth] = useState<any>(585)
  const [maxHeight, setMaxHeight] = useState<any>(308)
  const [materialType, setMaterialType] = useState<'standard' | 'plus' | 'sustainable' | 'default'>('default')
  const [adhesiveType, setAdhesiveType] = useState('1')
  const [showMaterialFilter, setShowMaterialFilter] = useState<boolean>(true)
  const maxWidthRef = useRef(maxWidth)
  const maxHeightRef = useRef(maxHeight)
  const [multiSelectForFinishing, setMultiSelectForFinishing] = useState<boolean>(false)
  const [foilHeading, setFoilHeading] = useState<string | undefined>()
  const windowSize = useWindowSize()
  const breakpointStringTruncation =
    (windowSize?.width > 991 && windowSize?.width < 1273) || (windowSize?.width > 599 && windowSize?.width < 857)

  // correct dimensions for sheets
  useEffect(() => {
    const maxValue = (fieldName, optionName) => {
      const field: any = fields?.find((field) => field.key === fieldName)
      return field?.options.find((option) => option.key === optionName).max ?? 0
    }
    setMaxHeight(maxValue('dimension', 'height'))
    setMaxWidth(maxValue('dimension', 'width'))
  }, [fields])

  useEffect(() => {
    maxWidthRef.current = maxWidth
    maxHeightRef.current = maxHeight
  }, [maxWidth, maxHeight])

  //for getting the initial paper when label_type and adhesive changes
  const [initialPaperId, setInitialPaperId] = useState<string>(selection.paper)

  //for correctly filtering papers based on standard, sustainable or plus
  const materialOptions = useCallback(
    (materialType: 'standard' | 'sustainable' | 'plus' | 'default'): any[] => {
      const paperField = fields.find(({ key }) => key === 'paper')

      let validIds: number[] = paperField?.options.map((item) => +item.id) ?? []

      if (materialType === 'standard') {
        validIds = standardMaterial
      } else if (materialType === 'sustainable') {
        validIds = sustainableMaterial
      } else if (materialType === 'plus') {
        validIds = plusMaterial
      } else if (materialType === 'default') {
        validIds
      }

      return paperField?.options.filter(({ id }) => validIds.includes(+id)) ?? []
    },
    [fields, standardMaterial, sustainableMaterial, plusMaterial]
  )

  //This useEffect is for setting the initial paper selection
  useEffect(() => {
    if (selection._id && selection.paper_locked) {
      // Persist the selected paper when editing an order from the cart
      setInitialPaperId(selection.paper)
      // Hide/show material filter options
      setShowMaterialFilter(
        materialOptions('standard').length > 0 &&
          materialOptions('sustainable').length > 0 &&
          materialOptions('plus').length > 0
      )
    } else if ((selection.label_type || selection.adhesive) && !selection.paper_locked) {
      // When editing, the customer changes the label type or adhesive, correclty fetch the initial paper
      setInitialPaperId(materialOptions(materialType)[0]['id'])
    } else {
      // For normal ordering (not from cart)
      setInitialPaperId(materialOptions(materialType)[0]['id'])
    }
  }, [fields, selection.paper_locked, selection._id, selection.label_type, selection.adhesive])

  const handleChange = useCallback(
    (optionId, value) => {
      const newSelection = {
        ...selection,
        [optionId]: value
      }
      if (optionId === 'quantity' && serviceType && ['pro', 'smart', 'premium', 'basic'].includes(serviceType)) {
        newSelection['quantity'] = 10
      }
      if (optionId === 'sorts' && serviceType === 'premium') {
        newSelection['sorts'] = 1
      }

      updateSelection(newSelection)
    },
    [selection, updateSelection]
  )

  const handleBlur = useCallback(
    (optionId, value) => {
      mutate({
        ...selection,
        [optionId]: value
      })
    },
    [selection, mutate]
  )

  const handleSelect = useCallback(
    (optionId, value, relatedFieldId, relatedField, key) => {
      if (key === 'relief_embossing') {
        setFoilHeading(getCorrectFoilName(key))
      } else if (key === 'foil') {
        setFoilHeading(undefined)
      }

      const newSelection = {
        ...selection,
        [optionId]: value,
        [relatedField]: relatedFieldId,
        ...(key === 'no_finishing' || key === 'blind_embossing'
          ? {
              foil: initialSelection['foil'],
              embossing_tool_material: initialSelection['embossing_tool_material']
            }
          : {}),
        ...(key === 'relief_embossing' ? { embossing_tool_material: initialSelection['embossing_tool_material'] } : {}),
        // remove lock from paper when label_type/adhesive changes
        ...(optionId === 'adhesive' || optionId === 'label_type' ? { paper_locked: false } : {})
      }

      setMultiSelectForFinishing(shouldEnableMultiSelect(key, newSelection))
      updateSelection(newSelection)
      mutate(newSelection)

      optionId === 'adhesive' && setAdhesiveType(key)

      //If the adhesive is removible or label_type is sheet set the materialType to default
      if (key === 'sheet' || (optionId === 'adhesive' && key === '2')) {
        setShowMaterialFilter(false)
        setMaterialType('default')
      } else {
        setShowMaterialFilter(true)
      }
    },
    [mutate, selection, updateSelection, maxWidth, maxHeight]
  )

  //This will ensure `paper` is updated when label_type or adhesive or materialType changes
  useEffect(() => {
    const selectedOption = materialOptions(materialType)[0]

    if (selection.label_type || selection.adhesive) {
      handleInitalPaperSelection(selection)
    }

    if (materialType !== 'default' && selectedOption) {
      handleSelectRadioVertical(
        'paper',
        materialOptions(materialType)[0]['id'],
        materialOptions(materialType)[0]['relatedFieldId'],
        materialOptions(materialType)[0]['relatedField'],
        0
      )
    }
  }, [selection.label_type, selection.adhesive, materialType, initialPaperId])

  const handleInitalPaperSelection = (newSelection) => {
    newSelection['paper'] = initialPaperId
    //correct max dimension values for label_types
    newSelection['width'] = Math.min(newSelection['width'], maxWidthRef.current)
    newSelection['height'] = Math.min(newSelection['height'], maxHeightRef.current)

    updateSelection(newSelection)
    mutate(newSelection)
  }

  const handleSelectRadioVertical = useCallback(
    (optionId, value, relatedFieldId, relatedField, index) => {
      setNewIndex(index)
      setSelectedMaterialId(value)
      const newSelection = {
        ...selection,
        [optionId]: value,
        [relatedField]: relatedFieldId
      }
      updateSelection(newSelection)
      mutate(newSelection)
    },
    [mutate, selection, updateSelection]
  )

  const handleSelectCheckbox = useCallback(
    (optionId, key, nextFieldId, relatedField, relatedFieldId) => {
      const newSelection = {
        ...selection,
        [optionId]: nextFieldId,
        [relatedField]: selection[relatedField]?.filter((item) => item !== relatedFieldId)
      }

      key === 'false' ? setKeyType(true) : setKeyType(false)

      updateSelection(newSelection)
      mutate(newSelection)
    },
    [mutate, selection, updateSelection]
  )

  const handleCheckbox = useCallback(
    (optionId, value) => {
      const newSelection = {
        ...selection,
        [optionId]: selection[optionId]?.includes(value)
          ? selection[optionId]?.filter((item) => item !== value) || []
          : [...(selection[optionId] || []), value]
      }

      updateSelection(newSelection)
      mutate(newSelection)
    },
    [mutate, selection, updateSelection]
  )

  const shouldEnableMultiSelect = (key: string, newSelection) =>
    newSelection['finishing'] === 117 && newSelection['foil'] !== initialSelection['foil']

  const handleMultipleSelect = useCallback(
    (checked: boolean, key) => {
      if (key === 'foil' || key === 'digital_foil') {
        return multiSelectForFinishing ? multiSelectForFinishing : checked
      } else {
        return checked
      }
    },
    [multiSelectForFinishing]
  )

  const showField = useCallback(
    (excludes): boolean => {
      if (!excludes) return true
      const isExcluded = excludes.some(({ id, value }) => selection[id] === value)
      return !isExcluded
    },
    [selection]
  )

  const isDisabled = useCallback(
    (disableList): boolean => {
      if (!disableList) return false
      const isDisabled = disableList.some(({ key, value }) => selection[key] === value)
      return isDisabled
    },
    [selection]
  )

  const checkCorrectSize = useCallback((value: string, max?: string, min?: string) => {
    const maxSize = Number(max)
    const minSize = Number(min)
    const currentValue = Number(value)

    if (currentValue > maxSize) return maxSize
    if (currentValue < minSize) return minSize
    return currentValue
  }, [])

  const handleInputDisabled = useCallback(
    (filedKey: string, optionKey: string) => {
      if (filedKey !== 'assembly') return false
      if (optionKey === 'labels_per_roll' && selection?.max_roll_diameter > 0) {
        return true
      }

      if (optionKey === 'max_roll_diameter' && selection?.labels_per_roll > 0) {
        return true
      }

      return false
    },
    [selection]
  )

  const activeDropDown = useCallback(
    (id, index) => {
      const clickedElement = vRadioRef.current[index]
      const childLabelElem = clickedElement?.querySelector('label')
      const parentWrapper = relativeParentRef.current

      if (clickedElement && parentWrapper && childLabelElem) {
        const { top } = clickedElement?.getBoundingClientRect()
        const { top: parentWrapperTop } = parentWrapper.getBoundingClientRect()
        setShowDropdown({
          state: true,
          id: id,
          top: `${top - parentWrapperTop + childLabelElem.clientHeight + 10}px`,
          index: index,
          closeCurrent: true
        })
      }
    },
    [showDropdown]
  )

  const adhesiveName = useCallback(
    (id: number | undefined): any => {
      return fields.find(({ key }) => key === 'adhesive')?.options.find(({ key }) => key === id?.toString())?.name
    },
    [fields]
  )

  const truncateString = (str, maxLength) => {
    return str.length > maxLength ? str.slice(0, maxLength) + '' : str
  }

  const getCorrectFoilName = useCallback(
    (keyString) => {
      return fields.find(({ key }) => key === 'finishing')?.options.find(({ key }) => key === keyString?.toString())
        ?.name
    },
    [fields]
  )

  useEffect(() => {
    fields.forEach((field, index) => {
      if (!field.force_value) return
      const { id, key, value, nextFieldId, relatedFieldId, relatedField } = field.force_value
      const onChangeTypes: Partial<Record<FieldType, () => void>> = {
        ['textarea']: () => handleChange(key, value),
        ['input-number']: () => handleChange(key, value),
        ['range']: () => handleChange(id, value),
        ['select-radio']: () => handleSelect(field.key, value, relatedFieldId, relatedField, key),
        ['select-radio-vertical']: () => handleSelectRadioVertical(field.key, id, relatedFieldId, relatedField, index),
        ['select-card-checkbox']: () => handleSelectCheckbox(field.key, key, nextFieldId, relatedFieldId, relatedField),
        ['checkbox']: () => handleCheckbox(field.key, id)
      }
      onChangeTypes[field.type]?.()
    })

    if (reset) {
      setResetFalse && setResetFalse()
      setSelectedMaterialId(selection.paper)
      setMaterialType('default')
      setShowMaterialFilter(true)
      setAdhesiveType('1')
    }

    if (showDropdown.state) {
      activeDropDown(showDropdown.id, newIndex)
    }
  }, [fields, showDropdown.state, selectedMaterialId, step, reset])

  return (
    <>
      {getCalculatorConstants(serviceType)
        .filter((el) => el.step === step)[0]
        ?.fields.map((subfield, index) => (
          <Fragment key={index}>
            {fields
              .filter((er) => er.key == subfield.key)
              .map((field) => (
                <Fragment key={field?.key}>
                  <>
                    {showField(field?.exclude) && (
                      <S.Block className={`tour-${field.key}`}>
                        <Text
                          size='lg'
                          family='heading'
                          weight='bold'
                          margin='0 0 0.8rem'
                          color={selection[field?.key] ? 'normal' : 'danger'}
                        >
                          {field.key === 'foil' ? foilHeading ?? field.displayName : field.displayName}
                          {field.hint && (
                            <S.Hint>
                              <Tooltip title={field.hint}>
                                <Icon name='info' />
                              </Tooltip>
                            </S.Hint>
                          )}

                          {!selection[field?.key] && <Icon name='warning' size='s4' />}
                        </Text>

                        <S.Option>
                          {field.type === 'input-number' && (
                            <>
                              {field?.options?.map(
                                ({ id, name, key, unit, min, max, description, lock }: IFieldOption) => (
                                  <>
                                    {/* show quantity field except for artwork service calulator */}
                                    {serviceType !== 'smart' && serviceType !== 'pro' && (
                                      <S.CBlock>
                                        <Input
                                          dimension='large'
                                          type='number'
                                          min={min}
                                          max={max}
                                          key={id}
                                          id={id}
                                          name={name}
                                          description={description}
                                          disabled={lock == 1 || handleInputDisabled(field.key, key)}
                                          label={`${name} ${unit ?? ''}`}
                                          value={selection[key]}
                                          onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                                            handleChange(key, Number(event.currentTarget.value).toFixed())
                                          }}
                                          onBlur={(event: ChangeEvent<HTMLInputElement>): void => {
                                            const { value } = event.currentTarget

                                            const valueFormatted = checkCorrectSize(value, max, min)
                                            handleBlur(key, Number(valueFormatted).toFixed())
                                            handleChange(key, Number(valueFormatted).toFixed())
                                          }}
                                          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                                            if (event.key === 'Enter') {
                                              const { value } = event.currentTarget

                                              const valueFormatted = checkCorrectSize(value, max, min)
                                              handleBlur(key, Number(valueFormatted).toFixed())
                                              handleChange(key, Number(valueFormatted).toFixed())
                                            }
                                          }}
                                          style={{ width: field.key === 'assembly' ? '16rem' : 'auto' }}
                                        />
                                      </S.CBlock>
                                    )}

                                    {/* hide quantity field for artwork service calulator */}
                                    {(serviceType === 'smart' || serviceType === 'pro') && key !== 'quantity' && (
                                      <S.CBlock>
                                        <Input
                                          dimension='large'
                                          type='number'
                                          min={min}
                                          max={max}
                                          key={id}
                                          id={id}
                                          name={name}
                                          description={description}
                                          disabled={lock == 1 || handleInputDisabled(field.key, key)}
                                          label={`${name} ${unit ?? ''}`}
                                          value={selection[key]}
                                          onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                                            handleChange(key, Number(event.currentTarget.value).toFixed())
                                          }}
                                          onBlur={(event: ChangeEvent<HTMLInputElement>): void => {
                                            const { value } = event.currentTarget

                                            const valueFormatted = checkCorrectSize(value, max, min)
                                            handleBlur(key, Number(valueFormatted).toFixed())
                                            handleChange(key, Number(valueFormatted).toFixed())
                                          }}
                                          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                                            if (event.key === 'Enter') {
                                              const { value } = event.currentTarget

                                              const valueFormatted = checkCorrectSize(value, max, min)
                                              handleBlur(key, Number(valueFormatted).toFixed())
                                              handleChange(key, Number(valueFormatted).toFixed())
                                            }
                                          }}
                                          style={{ width: field.key === 'assembly' ? '16rem' : 'auto' }}
                                        />
                                      </S.CBlock>
                                    )}
                                  </>
                                )
                              )}
                            </>
                          )}

                          {field.type === 'select-radio' && (
                            <>
                              {field?.options?.map(
                                ({
                                  id,
                                  name,
                                  icon,
                                  description,
                                  disabled,
                                  lock,
                                  key,
                                  warning,
                                  relatedFieldId,
                                  relatedField,
                                  priceVariant
                                }: IFieldOption) => (
                                  <SelectCard
                                    key={id}
                                    id={id}
                                    value={id}
                                    name={field.key}
                                    label={name}
                                    description={description}
                                    warning={warning}
                                    disabled={lock == 1 || isDisabled(disabled)}
                                    checked={id === selection[field.key]}
                                    onChange={() => handleSelect(field.key, id, relatedFieldId, relatedField, key)}
                                    iconName={icon}
                                    priceVariant={priceVariant}
                                  />
                                )
                              )}
                            </>
                          )}

                          {field.type === 'select-radio-vertical' && field.key === 'paper' && (
                            <S.VerticalRadioOption>
                              {/* Material filtering section */}
                              {showMaterialFilter && (
                                <S.FilterMaterial>
                                  <S.Filter
                                    role='button'
                                    tabIndex={0}
                                    onClick={() => setMaterialType('default')}
                                    onKeyPress={() => setMaterialType('default')}
                                    clicked={materialType === 'default'}
                                  >
                                    <Text weight='bold' margin='0.25rem'>
                                      {randomText?.all_materials}
                                    </Text>
                                  </S.Filter>
                                  <S.Filter
                                    role='button'
                                    tabIndex={0}
                                    onClick={() => setMaterialType('standard')}
                                    onKeyPress={() => setMaterialType('standard')}
                                    clicked={materialType === 'standard'}
                                  >
                                    <Text weight='bold' margin='0.25rem'>
                                      {randomText?.standard}
                                    </Text>
                                  </S.Filter>
                                  <S.Filter
                                    role='button'
                                    tabIndex={0}
                                    onClick={() => setMaterialType('plus')}
                                    onKeyPress={() => setMaterialType('plus')}
                                    clicked={materialType === 'plus'}
                                  >
                                    <Text weight='bold' margin='0.25rem'>
                                      {randomText?.plus_material}
                                    </Text>
                                  </S.Filter>
                                  <S.Filter
                                    role='button'
                                    tabIndex={0}
                                    onClick={() => setMaterialType('sustainable')}
                                    onKeyPress={() => setMaterialType('sustainable')}
                                    clicked={materialType === 'sustainable'}
                                  >
                                    <Text weight='bold' color='olive-green' margin='0.25rem'>
                                      {randomText?.sustainable}
                                    </Text>
                                  </S.Filter>
                                </S.FilterMaterial>
                              )}

                              <S.Option className='v-option-wrapper' ref={relativeParentRef}>
                                {materialOptions(materialType).map(
                                  (
                                    {
                                      id,
                                      name,
                                      description,
                                      icon,
                                      disabled,
                                      lock,
                                      warning,
                                      relatedFieldId,
                                      relatedField
                                    }: IFieldOption,
                                    index
                                  ) => (
                                    <div
                                      className={
                                        showDropdown.state && +id === showDropdown.id
                                          ? 'v-select-card dropdown-active'
                                          : 'v-select-card'
                                      }
                                      ref={(ref) => (vRadioRef.current[index] = ref)}
                                    >
                                      <SelectForCalcMaterials
                                        key={id}
                                        id={id}
                                        value={id}
                                        name={field.key}
                                        label={truncateString(name, breakpointStringTruncation ? 21 : 34)}
                                        description={description}
                                        disabled={lock == 1 || isDisabled(disabled)}
                                        checked={id === selection[field.key]}
                                        openDropdown={showDropdown.state && +id === showDropdown.id}
                                        setOpenDropdown={setShowDropdown}
                                        onChange={() =>
                                          handleSelectRadioVertical(field.key, id, relatedFieldId, relatedField, index)
                                        }
                                      />
                                    </div>
                                  )
                                )}
                                {/* Material dropdown section */}
                                {showDropdown.state && +selection['paper'] === showDropdown.id && (
                                  <S.OptionDropdown style={{ top: showDropdown.top }} ref={optionWrapperSet}>
                                    {/* Material API */}
                                    <>
                                      <S.MaterialImage>
                                        <Image src={materialData?.image_url} alt='paper' width={165} height={220} />
                                      </S.MaterialImage>
                                      <div>
                                        <Text color='weak' margin='1rem 0 0 1rem' padding='0'>
                                          Material
                                        </Text>
                                        <Text weight='bold' margin='0 0 0.5rem 1rem'>
                                          {materialData?.name}
                                        </Text>
                                        <S.MaterialDetails>
                                          <S.MaterialDescription>
                                            <>
                                              <List kind='gold'>
                                                <ListItem
                                                  key='key1'
                                                  iconName={'liquid-resistance' as IconName}
                                                  iconSize={'s3' as IconSizes}
                                                  weight={'normal'}
                                                  border={'bottom'}
                                                >
                                                  {Array.from(
                                                    { length: materialData?.liquid_resistance as number },
                                                    (_) => (
                                                      <S.IconAligner>
                                                        <Icon name='circle-filled' size='s2' />
                                                      </S.IconAligner>
                                                    )
                                                  )}
                                                  {Array.from(
                                                    { length: 3 - (materialData?.liquid_resistance as number) },
                                                    (_) => (
                                                      <S.IconAligner>
                                                        <Icon name='circle-unfilled' size='s2' />
                                                      </S.IconAligner>
                                                    )
                                                  )}
                                                </ListItem>
                                                <ListItem
                                                  key='key2'
                                                  iconName={'new-durability' as IconName}
                                                  iconSize={'s3' as IconSizes}
                                                  weight={'normal'}
                                                  border={'bottom'}
                                                >
                                                  <Text margin='0'>{materialData?.min_temperature} °C </Text>
                                                  <Image
                                                    src={`/icons/temperature.svg`}
                                                    alt='temperature'
                                                    width={50}
                                                    height={20}
                                                  />
                                                  <Text margin='0'> {materialData?.max_temperature} °C </Text>
                                                </ListItem>
                                                <ListItem
                                                  key='key3'
                                                  iconName={'new-permanent' as IconName}
                                                  iconSize={'s3' as IconSizes}
                                                  weight={'normal'}
                                                  border={'bottom'}
                                                >
                                                  <Text margin='0'>
                                                    {!adhesiveName(+adhesiveType) ? ' - ' : adhesiveName(+adhesiveType)}
                                                  </Text>
                                                </ListItem>
                                                <ListItem
                                                  key='key4'
                                                  iconName={'recycle' as IconName}
                                                  iconSize={'s3' as IconSizes}
                                                  weight={'normal'}
                                                  border={'bottom'}
                                                >
                                                  <S.IconAligner>
                                                    <Icon name='eco' />
                                                  </S.IconAligner>
                                                  <S.IconAligner>
                                                    <Icon name='fsc' />
                                                  </S.IconAligner>

                                                  <Text margin='0'>
                                                    {!materialData?.certificate ? ' - ' : materialData?.certificate}{' '}
                                                  </Text>
                                                </ListItem>
                                                <S.IconDetails>
                                                  <Icon name='liquid-resistance' size='s2' />{' '}
                                                  {randomText?.liquid_resistance}
                                                  {'   '}
                                                  <Icon name='new-durability' size='s2' />
                                                  {'   '}
                                                  {randomText?.durability}
                                                  {'   '} <Icon name='new-permanent' size='s2' />
                                                  {randomText?.adhesive}
                                                </S.IconDetails>
                                              </List>
                                            </>
                                          </S.MaterialDescription>
                                          <S.MaterialRecommendation>
                                            <Text margin='0' color='weak'>
                                              {randomText?.recommendation}
                                            </Text>
                                            <Text lineHeight='1.4'>
                                              {!materialData?.recommendation ? '-' : materialData?.recommendation}
                                            </Text>
                                          </S.MaterialRecommendation>
                                        </S.MaterialDetails>
                                      </div>
                                    </>
                                  </S.OptionDropdown>
                                )}
                              </S.Option>
                            </S.VerticalRadioOption>
                          )}

                          {field.type === 'select-radio-vertical' && field.key === 'finishing' && (
                            <S.Option className='finishing-option' ref={relativeParentRef}>
                              {field?.options?.map(
                                ({
                                  id,
                                  name,
                                  description,
                                  icon,
                                  disabled,
                                  lock,
                                  warning,
                                  key,
                                  relatedFieldId,
                                  relatedField
                                }: IFieldOption) => (
                                  <SelectCard
                                    key={id}
                                    orientation='vertical'
                                    id={id}
                                    value={id}
                                    name={field.key}
                                    label={name}
                                    warning={warning}
                                    disabled={lock == 1 || isDisabled(disabled)}
                                    checked={handleMultipleSelect(id === selection[field.key], key)}
                                    onChange={() => handleSelect(field.key, id, relatedFieldId, relatedField, key)}
                                    giveBackground={`/img/${icon}.png`}
                                    iconName={icon}
                                    textClassName='finishing-text'
                                    checkbox={true}
                                  />
                                )
                              )}
                            </S.Option>
                          )}

                          {field.type === 'select-radio-vertical' &&
                            field.key !== 'paper' &&
                            field.key !== 'finishing' && (
                              <S.Option className='v-option-wrapper' ref={relativeParentRef}>
                                {field?.options?.map(
                                  ({
                                    id,
                                    name,
                                    description,
                                    icon,
                                    disabled,
                                    lock,
                                    warning,
                                    key,
                                    relatedFieldId,
                                    relatedField
                                  }: IFieldOption) => (
                                    <SelectCard
                                      key={id}
                                      orientation='vertical'
                                      id={id}
                                      value={id}
                                      name={field.key}
                                      label={name}
                                      description={description}
                                      warning={warning}
                                      disabled={lock == 1 || isDisabled(disabled)}
                                      checked={id === selection[field.key]}
                                      onChange={() => handleSelect(field.key, id, relatedFieldId, relatedField, key)}
                                      iconName={icon}
                                    />
                                  )
                                )}
                              </S.Option>
                            )}

                          {field.type === 'select-card-checkbox' && (
                            <>
                              {field?.options?.map(
                                ({
                                  id,
                                  name,
                                  description,
                                  key,
                                  nextFieldId,
                                  relatedFieldId,
                                  relatedField,
                                  icon
                                }: IFieldOption) => (
                                  <SelectCardCheckBox
                                    key={id}
                                    id={id}
                                    value={id}
                                    name={field.key}
                                    label={name}
                                    description={description}
                                    checked={key === 'false' ? false : true}
                                    kind='circle'
                                    subText=''
                                    rightEndText=''
                                    boxType='withIcon'
                                    iconName={icon}
                                    onChange={() =>
                                      handleSelectCheckbox(field.key, key, nextFieldId, relatedField, relatedFieldId)
                                    }
                                  />
                                )
                              )}
                            </>
                          )}

                          {field.type === 'checkbox' && (
                            <>
                              {field?.options?.map(({ id, name, description }: IFieldOption) => (
                                <Checkbox
                                  key={id}
                                  id={id}
                                  value={id}
                                  name={field.key}
                                  label={name}
                                  description={description}
                                  checked={selection[field.key].includes(id)}
                                  kind='square'
                                  onChange={() => handleCheckbox(field.key, id)}
                                />
                              ))}
                            </>
                          )}
                        </S.Option>

                        {field.error_message && (
                          <Text margin='0.8rem 0 0' color='danger' size='sm'>
                            {field.error_message}
                          </Text>
                        )}
                      </S.Block>
                    )}
                  </>
                </Fragment>
              ))}
          </Fragment>
        ))}
    </>
  )
}
